<template>
    <div class="search-page">
        <div
            v-if="listIsInFlight"
            class="loading-container"
        >
        </div>
        <!-- Search Controls -->
        <aside class="search-controls">
            <AutocompleteSuggestCustomers
                v-model="customer"
            />
            <ClInput
                v-model.trim="trackingNumber"
                label="Tracking or Reference Number"
                hideDetails
                outlined
            />
            <div class="action-buttons inputs-wrapper__input--med">
                <ClButton
                    color="primary"
                    buttonText="Search"
                    :disabled="searchInFlight"
                    @click="getSearchResults()"
                />
                <ClButton
                    color="white"
                    buttonText="Clear"
                    @click="clearButtonHandler"
                />
            </div>
            <AutocompleteSuggestSender
                v-model="sender"
            />
            <v-select
                v-model="mailType"
                :items="mailTypesList"
                itemText="friendly"
                itemValue="id"
                returnObject
                hideDetails
                outlined
                label="Type"
                dense
            />
            <v-select
                v-model="customerType"
                :items="customerTypesList"
                itemText="friendly"
                itemValue="id"
                returnObject
                hideDetails
                outlined
                label="Service"
                dense
            />
            <v-select
                v-model="statusType"
                :items="statusListArray"
                itemText="friendly"
                itemValue="id"
                hideDetails
                outlined
                label="Status"
                dense
                returnObject
            />
            <ClDateRange
                :start.sync="dateRangeStart"
                :end.sync="dateRangeEnd"
                startLabel="Start Date"
                endLabel="End Date"
                endsToday
            >
                <template #separator>
                    <div class="separator" ></div>
                </template>
            </ClDateRange>
        </aside>

        <!-- Search Results -->
        <CustomerSearchTable
            :resultList="resultList"
            :loading="searchInFlight"
        />

    </div>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Utils
import _ from 'lodash';
import DateUtil from '@/utils/DateUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';

// Constants
import STATUS_LIST from '@/constants/mail/status_list';

// Components
import AutocompleteSuggestCustomers from '@/components/AutocompleteSuggestCustomers';
import AutocompleteSuggestSender from '@/components/AutocompleteSuggestSender';
import CustomerSearchTable from './search/CustomerSearchTable';

export default {
    name: 'MailSearch',
    components: {
        AutocompleteSuggestSender,
        CustomerSearchTable,
        AutocompleteSuggestCustomers,
    },
    data() {
        return {
            DateUtil,
            statusListArray: _.values( STATUS_LIST ),
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
            resultList: 'Mail/Search/resultList',
            hasResults: 'Mail/Search/hasResults',
            listIsInFlight: 'Mail/listIsInFlight',
            mailTypesList: 'Mail/mailTypesList',
            customerTypesList: 'Mail/customerTypesList',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            mailSearchFilters: ( state ) => state.Mail.Search.filters,
            searchInFlight: ( state ) => state.Mail.Search.results.inFlight,
            hasSearched: ( state ) => state.Mail.Search.results.hasSearched,
            customerTypes: ( state ) => state.Mail.customerTypes.list,
        } ),
        customer: {
            get() {
                return this.mailSearchFilters.customer;
            },
            set( customer ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_CUSTOMER', { customer } );
            },
        },
        sender: {
            get() {
                return this.mailSearchFilters.sender;
            },
            set( sender ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_SENDER', { sender } );
            },
        },
        trackingNumber: {
            get() {
                return this.mailSearchFilters.trackingNumber;
            },
            set( trackingNumber ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_TRACKING_NUMBER', { trackingNumber } );
            },
        },
        mailType: {
            get() {
                return this.mailSearchFilters.mailType;
            },
            set( mailType ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_MAIL_TYPE', { mailType } );
            },
        },
        customerType: {
            get() {
                return this.mailSearchFilters.customerType;
            },
            set( customerType ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_CUSTOMER_TYPE', { customerType } );
            },
        },
        statusType: {
            get() {
                return this.mailSearchFilters.statusType;
            },
            set( statusType ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_STATUS_TYPE', { statusType } );
            },
        },
        dateRangeStart: {
            get() {
                return DateUtil.formatDate( this.mailSearchFilters.dateRange.start );
            },
            set( start ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_DATE_RANGE_START', { start: DateUtil.formatDate( start ) } );
            },
        },
        dateRangeEnd: {
            get() {
                return DateUtil.formatDate( this.mailSearchFilters.dateRange.end );
            },
            set( end ) {
                this.$store.commit( 'Mail/Search/SET_FILTER_DATE_RANGE_END', { end } );
            },
        },
    },
    methods: {
        async getSearchResults() {
            try {
                await this.$store.dispatch( 'Mail/Search/getList' );
            } catch ( error ) {
                await this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
            }
        },
        clearButtonHandler() {
            this.$store.dispatch( 'Mail/Search/reset' );
            this.customer = null;
            this.sender = '';
            this.dateRangeStart = '';
            this.dateRangeEnd = '';
        },
    },
};
</script>

<style lang="scss" scoped>
    .search-page {
        margin-top: 2rem;

        .search-controls {
            display: grid;
            grid-template-columns: 1fr;
            row-gap: 1rem;
            column-gap: 1rem;
            margin-bottom: 1rem;
            .separator {
                width: 2rem;
            }
            .date-range-container{
                ::v-deep .date-container {
                    width: 100%;
                }
            }

            .action-buttons {
                display: flex;
                justify-content: right;
                align-items: center;
                .v-btn {
                    &:first-child {
                        margin-right: 1rem;
                    }
                }

            }
        }
        @include mixins-above(tablet) {
            .search-controls {
                grid-template-columns: repeat( 3, 1fr );
                .date-range-container {
                    grid-column: 2/-1;
                }
            }
        }
    }
</style>
